import React, { lazy, Suspense, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Styles/AdminDashboard.css';
import '../../constants/styleConstants.css';
import logout_axios_client from '../../services/logout_url';
import { GeneralPopupComponent, PopupType } from '../../Components/GeneralPopupComponent';

const LOCAL_STORAGE_KEYS = { isLoggedIn: 'isLoggedIn' };

// Lazy stubs (replace with real imports, e.g. lazy(() => import('./Overview')))
const Overview = lazy(() => import('./Components/OverviewComponent'));
const Videos = lazy(() => import('./Components/VideoComponent'));
const Categories = lazy(() => import('./Components/CategoriesComponent'));
const Languages = lazy(() => import('./Components/LanguagesComponent'));
const ChangePassword = lazy(() => import('./Components/ChangePasswordComponent'));

type MenuKey = 'overview' | 'videos' | 'categories' |'languages'| 'changepassword';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState<MenuKey>('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [feedback, setFeedback] = useState<{ open: boolean; type: PopupType; title: string; message: string; } | null>(null);

  const handleSelect = (key: MenuKey | 'logout') => async () => {
    if (key === 'logout') {
      setLoggingOut(true);
      try {
        await logout_axios_client.post('logout');
      } catch (e: any) {
        console.error("Logout failed:", e);
        const message = e?.response?.data?.message || "Could not contact the server, but you have been logged out locally.";
        setFeedback({ open: true, type: 'error', title: 'Logout Error', message });
        // Wait a moment for the user to see the popup before redirecting
        await new Promise(resolve => setTimeout(resolve, 2000));
      } finally {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.isLoggedIn);
        navigate("/admin/login");
        // No need to set loggingOut to false as the component will unmount
      }
    } else {
      setActive(key);
    }
    setSidebarOpen(false);
  };

  const ActiveView = useMemo(() => {
    switch (active) {
      case 'overview': return Overview;
      case 'videos': return Videos;
      case 'categories': return Categories;
      case 'languages': return Languages;
      case 'changepassword': return ChangePassword;
      default: return Overview;
    }
  }, [active]);

  return (
    <div className="admin-dash">
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`} aria-label="Admin menu">
        <div className="brand">
          <div className="brand-mark" aria-hidden="true">Hameruha</div>
          <h1 className="brand-title">Admin</h1>
        </div>

        <nav className="nav" role="navigation">
          <button
            className={`nav-item ${active === 'overview' ? 'active' : ''}`}
            onClick={handleSelect('overview')}
            aria-current={active === 'overview' ? 'page' : undefined}
          >
            <span className="icon" aria-hidden="true">🏠</span>
            Overview
          </button>
          <button
            className={`nav-item ${active === 'videos' ? 'active' : ''}`}
            onClick={handleSelect('videos')}
            aria-current={active === 'videos' ? 'page' : undefined}
          >
            <span className="icon" aria-hidden="true">🎬</span>
            Videos
          </button>
          <button
            className={`nav-item ${active === 'categories' ? 'active' : ''}`}
            onClick={handleSelect('categories')}
            aria-current={active === 'categories' ? 'page' : undefined}
          >
            <span className="icon" aria-hidden="true">🏷️</span>
            Categories
          </button>
          <button
            className={`nav-item ${active === 'languages' ? 'active' : ''}`}
            onClick={handleSelect('languages')}
            aria-current={active === 'languages' ? 'page' : undefined}
          >
            <span className="icon" aria-hidden="true">🌐</span>
            Languages
          </button>
          <button
            className={`nav-item ${active === 'changepassword' ? 'active' : ''}`}
            onClick={handleSelect('changepassword')}
            aria-current={active === 'changepassword' ? 'page' : undefined}
          >
            <span className="icon" aria-hidden="true">🔒</span>
            Change password
          </button>

          <div className="spacer" aria-hidden="true" />

          <button className="nav-item danger" onClick={handleSelect('logout')} disabled={loggingOut}>
            <span className="icon" aria-hidden="true">🚪</span>
            {loggingOut ? 'Logging out…' : 'Logout'}
          </button>
        </nav>
      </aside>

      <section className="main">
        <header className="content-topbar">
          <button
            className="btn menu-toggle"
            onClick={() => setSidebarOpen(s => !s)}
            aria-label="Toggle menu"
          >
            ☰
          </button>
          <div className="content-title">
            {active === 'overview' && 'Overview'}
            {active === 'videos' && 'Videos'}
            {active === 'categories' && 'Categories'}
            {active === 'changepassword' && 'Change password'}
          </div>
          <div aria-hidden="true" style={{ width: 40 }} />
        </header>

        <div className="content-wrap">
          <Suspense fallback={<div className="panel">Loading…</div>}>
            <ActiveView />
          </Suspense>
        </div>
      </section>

      {/* Clickable overlay for mobile when sidebar is open */}
      {sidebarOpen && <button className="overlay" aria-label="Close menu" onClick={() => setSidebarOpen(false)} />}

      {/* Feedback popup for logout errors */}
      {feedback && (
        <GeneralPopupComponent
          isOpen={feedback.open}
          onClose={() => setFeedback(null)}
          type={feedback.type}
          title={feedback.title}
          message={feedback.message}
        />
      )}
    </div>
  );
};

export default AdminDashboard;